/**
 * DateRangePickerV2 Utilities
 *
 * Helper functions for converting presets to date ranges using date-fns.
 */
import {
  addDays,
  addMonths,
  addQuarters,
  addWeeks,
  addYears,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
  subYears,
} from 'date-fns';

import {
  CategoryConfig,
  DateRangeDefaultValue,
  DateRangePreset,
  DateRangePresetCategory,
  DateRangeV2,
} from './types';

/** Monday as week start (1 = Monday, 0 = Sunday) per European standard */
const WEEK_START_DAY = 1 as const;

/**
 * Checks if a value is a DateRangePreset enum value
 */
export function isDateRangePreset(value: DateRangeDefaultValue): value is DateRangePreset {
  return (
    typeof value === 'string' && Object.values(DateRangePreset).includes(value as DateRangePreset)
  );
}

/**
 * Converts a date range preset to actual start and end dates
 *
 * @param preset - The preset to convert
 * @returns DateRangeV2 with startDate and endDate
 */
export function getDateRangeFromPreset(preset: DateRangePreset): DateRangeV2 {
  const now = new Date();

  switch (preset) {
    // Day presets
    case DateRangePreset.Today:
      return {
        startDate: startOfDay(now),
        endDate: endOfDay(now),
      };
    case DateRangePreset.Yesterday:
      return {
        startDate: startOfDay(subDays(now, 1)),
        endDate: endOfDay(subDays(now, 1)),
      };
    case DateRangePreset.Tomorrow:
      return {
        startDate: startOfDay(addDays(now, 1)),
        endDate: endOfDay(addDays(now, 1)),
      };

    // Week presets
    case DateRangePreset.ThisWeek:
      return {
        startDate: startOfWeek(now, { weekStartsOn: WEEK_START_DAY }),
        endDate: endOfWeek(now, { weekStartsOn: WEEK_START_DAY }),
      };
    case DateRangePreset.LastWeek:
      return {
        startDate: startOfWeek(subWeeks(now, 1), { weekStartsOn: WEEK_START_DAY }),
        endDate: endOfWeek(subWeeks(now, 1), { weekStartsOn: WEEK_START_DAY }),
      };
    case DateRangePreset.NextWeek:
      return {
        startDate: startOfWeek(addWeeks(now, 1), { weekStartsOn: WEEK_START_DAY }),
        endDate: endOfWeek(addWeeks(now, 1), { weekStartsOn: WEEK_START_DAY }),
      };

    // Month presets
    case DateRangePreset.ThisMonth:
      return {
        startDate: startOfMonth(now),
        endDate: endOfMonth(now),
      };
    case DateRangePreset.LastMonth:
      return {
        startDate: startOfMonth(subMonths(now, 1)),
        endDate: endOfMonth(subMonths(now, 1)),
      };
    case DateRangePreset.NextMonth:
      return {
        startDate: startOfMonth(addMonths(now, 1)),
        endDate: endOfMonth(addMonths(now, 1)),
      };

    // Quarter presets
    case DateRangePreset.ThisQuarter:
      return {
        startDate: startOfQuarter(now),
        endDate: endOfQuarter(now),
      };
    case DateRangePreset.LastQuarter:
      return {
        startDate: startOfQuarter(subQuarters(now, 1)),
        endDate: endOfQuarter(subQuarters(now, 1)),
      };
    case DateRangePreset.NextQuarter:
      return {
        startDate: startOfQuarter(addQuarters(now, 1)),
        endDate: endOfQuarter(addQuarters(now, 1)),
      };

    // Year presets
    case DateRangePreset.ThisYear:
      return {
        startDate: startOfYear(now),
        endDate: endOfYear(now),
      };
    case DateRangePreset.LastYear:
      return {
        startDate: startOfYear(subYears(now, 1)),
        endDate: endOfYear(subYears(now, 1)),
      };
    case DateRangePreset.NextYear:
      return {
        startDate: startOfYear(addYears(now, 1)),
        endDate: endOfYear(addYears(now, 1)),
      };

    default: {
      // Exhaustive check
      const _exhaustiveCheck: never = preset;
      return _exhaustiveCheck;
    }
  }
}

/**
 * Returns the i18n translation key for a preset label
 *
 * @param preset - The preset to get label for
 * @returns Translation key string
 */
export function getPresetLabelKey(preset: DateRangePreset): string {
  return `common:dateRangePickerV2.presets.${preset}`;
}

/**
 * Returns the category label i18n key
 *
 * @param category - The category to get label for
 * @returns Translation key string
 */
export function getCategoryLabelKey(category: DateRangePresetCategory): string {
  const categoryLabels: Record<DateRangePresetCategory, string> = {
    [DateRangePresetCategory.Day]: 'common:dateRangePickerV2.categories.today',
    [DateRangePresetCategory.Week]: 'common:dateRangePickerV2.categories.thisWeek',
    [DateRangePresetCategory.Month]: 'common:dateRangePickerV2.categories.thisMonth',
    [DateRangePresetCategory.Quarter]: 'common:dateRangePickerV2.categories.thisQuarter',
    [DateRangePresetCategory.Year]: 'common:dateRangePickerV2.categories.thisYear',
  };
  return categoryLabels[category];
}

/**
 * Maps a preset to its parent category
 *
 * @param preset - The preset to categorize
 * @returns The category the preset belongs to
 */
export function getCategoryFromPreset(preset: DateRangePreset): DateRangePresetCategory {
  const dayPresets = [DateRangePreset.Today, DateRangePreset.Yesterday, DateRangePreset.Tomorrow];
  const weekPresets = [
    DateRangePreset.ThisWeek,
    DateRangePreset.LastWeek,
    DateRangePreset.NextWeek,
  ];
  const monthPresets = [
    DateRangePreset.ThisMonth,
    DateRangePreset.LastMonth,
    DateRangePreset.NextMonth,
  ];
  const quarterPresets = [
    DateRangePreset.ThisQuarter,
    DateRangePreset.LastQuarter,
    DateRangePreset.NextQuarter,
  ];

  if (dayPresets.includes(preset)) {
    return DateRangePresetCategory.Day;
  }
  if (weekPresets.includes(preset)) {
    return DateRangePresetCategory.Week;
  }
  if (monthPresets.includes(preset)) {
    return DateRangePresetCategory.Month;
  }
  if (quarterPresets.includes(preset)) {
    return DateRangePresetCategory.Quarter;
  }
  return DateRangePresetCategory.Year;
}

/**
 * Configuration for all preset categories and their options
 */
export const PRESET_CATEGORIES: CategoryConfig[] = [
  {
    category: DateRangePresetCategory.Day,
    labelKey: 'common:dateRangePickerV2.categories.today',
    presets: [
      { preset: DateRangePreset.Today, labelKey: 'common:dateRangePickerV2.presets.today' },
      { preset: DateRangePreset.Yesterday, labelKey: 'common:dateRangePickerV2.presets.yesterday' },
      { preset: DateRangePreset.Tomorrow, labelKey: 'common:dateRangePickerV2.presets.tomorrow' },
    ],
  },
  {
    category: DateRangePresetCategory.Week,
    labelKey: 'common:dateRangePickerV2.categories.thisWeek',
    presets: [
      { preset: DateRangePreset.ThisWeek, labelKey: 'common:dateRangePickerV2.presets.thisWeek' },
      { preset: DateRangePreset.LastWeek, labelKey: 'common:dateRangePickerV2.presets.lastWeek' },
      { preset: DateRangePreset.NextWeek, labelKey: 'common:dateRangePickerV2.presets.nextWeek' },
    ],
  },
  {
    category: DateRangePresetCategory.Month,
    labelKey: 'common:dateRangePickerV2.categories.thisMonth',
    presets: [
      { preset: DateRangePreset.ThisMonth, labelKey: 'common:dateRangePickerV2.presets.thisMonth' },
      { preset: DateRangePreset.LastMonth, labelKey: 'common:dateRangePickerV2.presets.lastMonth' },
      { preset: DateRangePreset.NextMonth, labelKey: 'common:dateRangePickerV2.presets.nextMonth' },
    ],
  },
  {
    category: DateRangePresetCategory.Quarter,
    labelKey: 'common:dateRangePickerV2.categories.thisQuarter',
    presets: [
      {
        preset: DateRangePreset.ThisQuarter,
        labelKey: 'common:dateRangePickerV2.presets.thisQuarter',
      },
      {
        preset: DateRangePreset.LastQuarter,
        labelKey: 'common:dateRangePickerV2.presets.lastQuarter',
      },
      {
        preset: DateRangePreset.NextQuarter,
        labelKey: 'common:dateRangePickerV2.presets.nextQuarter',
      },
    ],
  },
  {
    category: DateRangePresetCategory.Year,
    labelKey: 'common:dateRangePickerV2.categories.thisYear',
    presets: [
      { preset: DateRangePreset.ThisYear, labelKey: 'common:dateRangePickerV2.presets.thisYear' },
      { preset: DateRangePreset.LastYear, labelKey: 'common:dateRangePickerV2.presets.lastYear' },
      { preset: DateRangePreset.NextYear, labelKey: 'common:dateRangePickerV2.presets.nextYear' },
    ],
  },
];

/**
 * Detects which preset matches a given date range by comparing calendar dates.
 * Uses YYYY-MM-DD string comparison to avoid timezone-related mismatches
 * (e.g. UTC midnight vs local midnight).
 * Returns null if no preset matches (custom range).
 */
export function detectPresetFromDates(dates: DateRangeV2): DateRangePreset | null {
  const toDateKey = (d: Date) => format(d, 'yyyy-MM-dd');
  const startKey = toDateKey(dates.startDate);
  const endKey = toDateKey(dates.endDate);

  for (const preset of Object.values(DateRangePreset)) {
    const presetRange = getDateRangeFromPreset(preset);
    if (
      toDateKey(presetRange.startDate) === startKey &&
      toDateKey(presetRange.endDate) === endKey
    ) {
      return preset;
    }
  }
  return null;
}

/**
 * Checks if a preset belongs to a specific category
 *
 * @param preset - The preset to check
 * @param category - The category to check against
 * @returns True if preset belongs to category
 */
export function isPresetInCategory(
  preset: DateRangePreset | null,
  category: DateRangePresetCategory
): boolean {
  if (!preset) return false;
  return getCategoryFromPreset(preset) === category;
}
